import { Link } from 'react-router-dom';

import { readDraft } from './campaignDraft.js';

/**
 * The sheet and inventory, reachable from the table.
 *
 * They live on their own route and nothing on the play surface linked to them,
 * so checking what you were carrying meant going back to the lobby and finding
 * your character again — mid-scene.
 *
 * **A new tab, deliberately.** Navigating away unmounts the page and drops the
 * socket; a player who wanted to glance at their pack would be leaving the
 * table to do it. Two tabs for one character is already handled everywhere it
 * matters — the digest bookmark only moves when the *last* connection closes.
 *
 * Takes its id as a prop rather than reading the store, like the other panels:
 * zustand serves a server render the *initial* state, so a component that
 * subscribed could only ever be tested empty.
 */
export function SheetLink({ characterId }: { readonly characterId: string | null }) {
  // Nothing to link to before the snapshot says who you are. Absent rather
  // than disabled: a dead link is worse than no link.
  if (!characterId) return null;

  return (
    <a
      href={`/characters/${characterId}`}
      target="_blank"
      rel="noreferrer"
      className="block rounded-md border border-neutral-300 px-2 py-1 text-center text-sm dark:border-neutral-700"
    >
      Your sheet &amp; inventory ↗
    </a>
  );
}

/**
 * The way back from a sheet to the table it belongs to.
 *
 * **The sheet opens in a new tab**, so there is no history to go back through
 * — without this the only route back to the game is the lobby, which is a long
 * way round from the middle of a scene.
 *
 * A router link rather than an anchor: this one navigates *within* the tab it
 * is in, which is the tab the player opened for the sheet.
 */
export function BackToTableLink({ campaignId }: { readonly campaignId: string | undefined }) {
  // **A character with no seat still needs a way out.** This returned `null`
  // for one — a sheet rolled in the lobby and not yet joined to anything —
  // which made the page a dead end with no navigation whatsoever: the sheet
  // opens in its own tab, so there is no history either. That is the first
  // thing anybody sees after rolling a character, and it was the one screen
  // with no way off it.
  // With no seat, the best answer is wherever you came from — and the one
  // journey that lands here is rolling a character *while starting a table*,
  // which leaves a draft behind. Returning to the lobby instead would be
  // correct and would still lose you a click and a moment's doubt about
  // whether the half-written campaign survived.
  const draft = campaignId ? undefined : readDraft();
  const resuming = draft !== undefined && (draft.name !== '' || draft.premise !== '');
  const to = campaignId ? `/campaigns/${campaignId}` : resuming ? '/campaigns/new' : '/';
  const label = campaignId
    ? '← Back to the table'
    : resuming
      ? '← Back to starting your campaign'
      : '← Back to your characters';

  return (
    <Link to={to} className="inline-block text-sm underline underline-offset-2">
      {label}
    </Link>
  );
}
